import * as types from '../types';
import { IConversationsApi } from '../contracts';
import { HttpApi } from './HttpApi';

const enc = encodeURIComponent;

/** Only the filters that are set go on the query string (the server rejects blanks). */
function params(query: Record<string, string | number | boolean | undefined> = {}): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === '' || value === false) return;
    result[key] = typeof value === 'boolean' ? String(value) : value;
  });
  return result;
}

/** Two-way conversation inbox (tracker item 56). */
export class ConversationsHttpApi extends HttpApi implements IConversationsApi {
  async listConversations(query: types.ConversationListQuery = {}): Promise<types.ConversationPage> {
    return (await this.http.get<types.APIResponse<types.ConversationPage>>('/conversations', { params: params({ ...query }) })).data.data;
  }

  async getConversationUnreadCount(): Promise<types.UnreadCounts> {
    return (await this.http.get<types.APIResponse<types.UnreadCounts>>('/conversations/unread-count')).data.data;
  }

  async getConversation(id: string): Promise<types.ConversationDetail> {
    return (await this.http.get<types.APIResponse<types.ConversationDetail>>(`/conversations/${enc(id)}`)).data.data;
  }

  async createConversation(data: types.CreateConversationInput): Promise<types.ConversationSendResult> {
    return (await this.http.post<types.APIResponse<types.ConversationSendResult>>('/conversations', data)).data.data;
  }

  async replyToConversation(id: string, body: string, idempotencyKey?: string): Promise<types.ConversationSendResult> {
    return (await this.http.post<types.APIResponse<types.ConversationSendResult>>(`/conversations/${enc(id)}/reply`, { body, ...(idempotencyKey ? { idempotencyKey } : {}) })).data.data;
  }

  async addConversationNote(id: string, body: string): Promise<types.ConversationMessage> {
    return (await this.http.post<types.APIResponse<types.ConversationMessage>>(`/conversations/${enc(id)}/notes`, { body })).data.data;
  }

  async updateConversation(id: string, data: types.UpdateConversationInput): Promise<types.Conversation> {
    return (await this.http.patch<types.APIResponse<types.Conversation>>(`/conversations/${enc(id)}`, data)).data.data;
  }

  async markConversationRead(id: string): Promise<{ conversationId: string; lastReadAt: string }> {
    return (await this.http.post<types.APIResponse<{ conversationId: string; lastReadAt: string }>>(`/conversations/${enc(id)}/read`, {})).data.data;
  }

  async listUnmatchedConversations(query: { page?: number; limit?: number } = {}): Promise<types.ConversationPage<types.UnmatchedConversation>> {
    return (await this.http.get<types.APIResponse<types.ConversationPage<types.UnmatchedConversation>>>('/conversations/unmatched', { params: params({ ...query }) })).data.data;
  }

  async assignConversationCustomer(id: string, data: types.AssignCustomerInput): Promise<types.Conversation> {
    return (await this.http.post<types.APIResponse<types.Conversation>>(`/conversations/${enc(id)}/assign-customer`, data)).data.data;
  }

  async discardConversation(id: string): Promise<types.Conversation> {
    return (await this.http.post<types.APIResponse<types.Conversation>>(`/conversations/${enc(id)}/discard`, {})).data.data;
  }

  async getPortalMessages(): Promise<types.PortalThread> {
    return (await this.http.get<types.APIResponse<types.PortalThread>>('/portal/messages')).data.data;
  }

  async sendPortalMessage(body: string): Promise<types.PortalThread> {
    return (await this.http.post<types.APIResponse<types.PortalThread>>('/portal/messages', { body })).data.data;
  }

  async markPortalMessagesRead(): Promise<{ conversationId: string | null; unreadCount: number }> {
    return (await this.http.post<types.APIResponse<{ conversationId: string | null; unreadCount: number }>>('/portal/messages/read', {})).data.data;
  }
}
